import { Divider, Heading, Stack } from "@chakra-ui/react";
import React from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  ReferenceArea,
  ReferenceLine,
} from "recharts";
import { tooltipStyles } from "./ChartFormats/tooltipStyle";

export default function AveragePriceVsYearAreaChart(
  props,
  includeReserveNotMet
) {
  // Whether to include RNM auction values or not
  includeReserveNotMet = includeReserveNotMet || true;

  // Get the current year
  const currentYear = parseInt(new Date().getFullYear());

  // Get minimum year from the data
  const minimumYear = Math.min(
    ...props.data
      .map((item) => parseInt(item.auction_year))
      .filter((year) => !isNaN(year))
  );

  // format price to $140k or $1.4M
  const currencyLabelFormatter = (value) => {
    if (value >= 1000000) {
      return "$" + (value / 1000000).toFixed(1) + "M";
    } else {
      return "$" + parseInt(value / 1000) + "k";
    }
  };

  let averageData = [];
  if (props.isAggregated) {
    averageData = props.data.map((item) => ({
      auction_year: item.auction_year,
      averagePrice:
        item.averagePrice !== undefined
          ? item.averagePrice
          : item.avg_price || 0,
    }));
  } else {
    // Calculate the average price per year
    const averagePrices = props.data.reduce((accumulator, currentValue) => {
      const year = currentValue.auction_year;
      const price = currentValue.bid_price;
      const sold = currentValue.sold;

      if (!accumulator[year]) {
        accumulator[year] = { sum: 0, count: 0 };
      }

      if (!includeReserveNotMet) {
        if (sold === "True") {
          accumulator[year].sum += price;
          accumulator[year].count += 1;
        }
      } else {
        accumulator[year].sum += price;
        accumulator[year].count += 1;
      }

      return accumulator;
    }, {});

    averageData = Object.keys(averagePrices).map((year) => {
      return {
        auction_year: year,
        averagePrice: Math.round(
          averagePrices[year].sum / averagePrices[year].count
        ),
      };
    });
  }

  // Find the last available year in the data
  const lastYearInData = Math.max(
    ...averageData.map((item) => parseInt(item.auction_year))
  );

  // Create custom tooltip
  const formatTooltip = (value) => {
    return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    const averagePrice = payload[0];

    if (active) {
      return (
        <Stack className="custom-tooltip">
          <Heading size="md" color="#D3A346">{`Year: ${label}`}</Heading>
          <Divider borderColor="gray.400" />
          <Heading size="md" color={props.color}>{`Avg Price: ${formatTooltip(
            averagePrice.value
          )}`}</Heading>
        </Stack>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer
      width="100%"
      height={props.height ? props.height : 350}
    >
      <AreaChart
        data={averageData}
        margin={{
          top: 20,
          right: 15,
          bottom: 10,
          left: 15,
        }}
      >
        <defs>
          <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={props.color} stopOpacity={0.8} />
            <stop offset="95%" stopColor={props.color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <defs>
          <linearGradient id="grayColorGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#747070" stopOpacity={0.8} />{" "}
            <stop offset="95%" stopColor="#747070" stopOpacity={0} />{" "}
          </linearGradient>
        </defs>
        <XAxis
          dataKey="auction_year"
          stroke={props.labelColor}
          interval={0}
          tick={{ fontSize: 12 }}
          dy={5}
          angle={-45} // Rotate the x-axis labels
          textAnchor="end" // Align the rotated labels
          tickCount={currentYear + 1 - minimumYear}
          type="number"
          domain={[minimumYear, currentYear]}
        ></XAxis>
        <YAxis
          dataKey="averagePrice"
          stroke={props.labelColor}
          tickFormatter={currencyLabelFormatter}
        ></YAxis>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <Tooltip content={<CustomTooltip />} wrapperStyle={tooltipStyles} />
        <Area
          type="monotone"
          dataKey="averagePrice"
          stroke={props.color}
          fill="url(#colorGradient)"
          strokeWidth={3}
          isAnimationActive={false}
          dot={{
            fill: "#D3A346", // Change the color of the dots to yellow
            stroke: "#D3A346", // Change the outline color of the dots to yellow
            strokeWidth: 4,
            r: 2,
          }}
          activeDot={{
            fill: "#D3A346", // Change the color of the dots to yellow
            stroke: "#D3A346", // Change the outline color of the dots to yellow
            r: 6,
          }}
        />
        {lastYearInData !== currentYear ? (
          <>
            <ReferenceLine
              segment={[
                {
                  x: lastYearInData,
                  y:
                    averageData?.length > 0
                      ? averageData[averageData.length - 1]?.averagePrice
                      : undefined,
                },
                {
                  x: currentYear,
                  y:
                    averageData?.length > 0
                      ? averageData[averageData.length - 1]?.averagePrice
                      : undefined,
                },
              ]}
              stroke={props.color}
              strokeWidth={3}
              // label={
              //   <Label
              //     value="NO SALES"
              //     position="bottom"
              //     style={{ fill: props.color, fontSize: "80%" }}
              //   />
              // }
              strokeDasharray="3 3" // Optional: Dashed line style
            />
            <ReferenceArea
              fill="url(#grayColorGradient)"
              // y2={averageData?.slice(-1)[0].averagePrice}
              y2={
                averageData?.length > 0
                  ? averageData[averageData.length - 1]?.averagePrice
                  : undefined
              }
              x1={lastYearInData}
              x2={currentYear}
            />
          </>
        ) : null}
      </AreaChart>
    </ResponsiveContainer>
  );
}
